import { useEffect, useState } from 'react';
import type { ObjectApartmentSummary } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { BookingReassignmentDialogBody } from './BookingReassignmentDialogBody';
import { useBookingReassignment } from './useBookingReassignment';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  sourcePropertyId: string;
  sourcePropertyName: string;
  checkInDate: string;
  checkOutDate: string;
  properties: ObjectApartmentSummary[];
  initialTargetPropertyId?: string | null;
  /** Ziele, die schon vor dem Server feststehen als belegt, mit Grund; sie bleiben sichtbar, aber nicht wählbar. */
  unavailableTargets?: ReadonlyMap<string, string>;
  onCompleted?: () => void;
}

export function BookingReassignmentDialog(props: Props) {
  const [target, setTarget] = useState(props.initialTargetPropertyId ?? '');
  const [reason, setReason] = useState('');
  const state = useBookingReassignment({
    bookingId: props.bookingId,
    targetPropertyId: target,
    reason,
    open: props.open,
    onCompleted: () => {
      props.onOpenChange(false);
      props.onCompleted?.();
    },
  });
  const options = props.properties.filter(
    (property) => property.id !== props.sourcePropertyId && property.status === 'Active',
  );
  const targetProperty = options.find((property) => property.id === target);
  const targetName = targetProperty?.adminDisplayName ?? targetProperty?.name ?? 'Ziel';

  useEffect(() => {
    if (!props.open) return;
    setTarget(props.initialTargetPropertyId ?? '');
    setReason('');
    state.resetCommand();
    // The reset is intentionally tied to opening and the drag target, not query state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.initialTargetPropertyId]);

  const updateTarget = (value: string) => {
    setTarget(value);
    state.resetCommand();
  };
  const updateReason = (value: string) => {
    setReason(value);
    state.resetCommand();
  };
  const kind = state.preview.data?.kind;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apartment wechseln</DialogTitle>
          <DialogDescription>
            Die Aufenthaltsdaten bleiben unverändert. StayOn prüft das Ziel vor dem Speichern.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <BookingReassignmentDialogBody
            sourceName={props.sourcePropertyName}
            targetName={targetName}
            target={target}
            options={options}
            unavailable={props.unavailableTargets}
            checkInDate={props.checkInDate}
            checkOutDate={props.checkOutDate}
            kind={kind}
            checking={state.preview.isFetching}
            problem={state.problem}
            reason={reason}
            onTargetChange={updateTarget}
            onReasonChange={updateReason}
          />
        </DialogBody>
        <DialogFooter>
          <Button onClick={() => props.onOpenChange(false)} disabled={state.apply.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={state.apply.isPending}
            disabled={!state.preview.data || state.preview.isFetching}
            onClick={() => state.apply.mutate()}
          >
            {kind === 'Swap' ? 'Tausch bestätigen' : 'Verschieben bestätigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
